/**
 * CommunicationApiClient
 * 
 * Handles subscriptions, blocking, unified inbox, and communication preferences
 */

import { BaseApiClient } from './BaseApiClient';
import type { 
  Subscription, 
  BlockedMerchant, 
  UnifiedMessage,
  CommunicationPreferences 
} from '../../types/api/user';

export class CommunicationApiClient extends BaseApiClient {
  /**
   * Subscribe to a merchant manually
   */
  async subscribeToMerchant(merchantId: string): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(`/user/subscriptions/${merchantId}`, {});
  }

  /**
   * Unsubscribe from a merchant
   */
  async unsubscribeFromMerchant(merchantId: string): Promise<{ success: boolean; message: string }> {
    return this.delete<{ success: boolean; message: string }>(`/user/subscriptions/${merchantId}`);
  }

  /**
   * Get user's subscribed merchants
   */
  async getSubscriptions(): Promise<{ merchants: Array<{merchantId: string; merchantName: string; subscribedAt: string}> }> {
    return this.get<{ merchants: Array<{merchantId: string; merchantName: string; subscribedAt: string}> }>('/user/subscriptions');
  }

  /**
   * Block a merchant
   */
  async blockMerchant(merchantId: string, reason?: string): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>('/user/blocked-merchants', {
      merchantId,
      reason
    });
  }

  /**
   * Get blocked merchants list
   */
  async getBlockedMerchants(): Promise<{ blockedMerchants: BlockedMerchant[] }> {
    return this.get<{ blockedMerchants: BlockedMerchant[] }>('/user/blocked-merchants');
  }

  /**
   * Unblock a merchant
   */
  async unblockMerchant(merchantId: string): Promise<{ success: boolean; message: string }> {
    return this.delete<{ success: boolean; message: string }>(`/user/blocked-merchants/${merchantId}`);
  }

  /**
   * Get unified inbox messages
   * Returns empty result if endpoint is not available (404) - this is expected
   * until the backend endpoint is implemented
   */
  async getInboxMessages(filters?: {
    unreadOnly?: boolean;
    merchantId?: string;
    type?: 'announcement' | 'conversation' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<{
    messages: UnifiedMessage[];
    unreadCount: number;
    totalCount: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.unreadOnly) params.append('unreadOnly', 'true');
    if (filters?.merchantId) params.append('merchantId', filters.merchantId);
    if (filters?.type && filters.type !== 'all') params.append('type', filters.type);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    try {
      return await this.get<{
        messages: UnifiedMessage[];
        unreadCount: number;
        totalCount: number;
      }>(`/user/messages/inbox?${params}`);
    } catch (error: any) {
      // Handle 404 gracefully - endpoint may not be implemented yet
      // BaseApiClient throws errors with messages like "not_found" or "HTTP 404: Not Found"
      
      // Log the error details for debugging
      if (import.meta.env.DEV) {
        console.log('🔍 [CommunicationApiClient] Caught error in getInboxMessages:', {
          error: error,
          errorType: typeof error,
          errorMessage: error?.message,
          errorString: String(error),
          errorKeys: error ? Object.keys(error) : []
        });
      }
      
      const errorMessage = error?.message || String(error) || '';
      const errorString = errorMessage.toLowerCase().trim();
      const errorToString = String(error).toLowerCase();
      
      // Check for various 404 indicators - be very permissive
      // The error message from BaseApiClient is "not_found" for 404 responses
      const isNotFound = 
        // Check error message (exact match for "not_found")
        errorString === 'not_found' ||
        // Check error message (contains variations)
        errorString.includes('404') || 
        errorString.includes('not found') ||
        errorString.includes('notfound') ||
        // Check string representation
        errorToString === 'not_found' ||
        errorToString.includes('404') ||
        errorToString.includes('not_found') ||
        errorToString.includes('not found') ||
        // Check status codes
        error?.status === 404 ||
        error?.response?.status === 404 ||
        // Check if it's any 4xx error with "not" in the message
        (error?.response?.status >= 400 && error?.response?.status < 500 && (errorString.includes('not') || errorToString.includes('not')));
      
      if (isNotFound) {
        // Silently return empty result - this is expected until backend implements the endpoint
        if (import.meta.env.DEV) {
          console.log('📭 [CommunicationApiClient] V1 inbox endpoint not available (404/not_found) - returning empty result', {
            errorMessage: errorMessage,
            errorString: errorString,
            errorToString: errorToString,
            errorStatus: error?.status,
            responseStatus: error?.response?.status,
            isNotFound: true
          });
        }
        return {
          messages: [],
          unreadCount: 0,
          totalCount: 0
        };
      }
      
      // Log if we're not treating it as 404
      if (import.meta.env.DEV) {
        console.warn('⚠️ [CommunicationApiClient] Error not recognized as 404, re-throwing:', {
          errorMessage: errorMessage,
          errorString: errorString,
          isNotFound: false
        });
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Mark message as read
   */
  async markMessageAsRead(messageId: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/user/messages/${messageId}/read`);
  }

  /**
   * Mark all messages as read
   */
  async markAllMessagesAsRead(merchantId?: string): Promise<{ success: boolean; markedCount: number }> {
    return this.post<{ success: boolean; markedCount: number }>('/user/messages/mark-all-read', {
      merchantId
    });
  }

  /**
   * Get communication preferences
   */
  async getCommunicationPreferences(): Promise<CommunicationPreferences> {
    const response = await this.get<{ notificationPreferences: CommunicationPreferences }>('/profile/preferences/notification');
    return response.notificationPreferences;
  }

  /**
   * Update communication preferences
   */
  async updateCommunicationPreferences(preferences: CommunicationPreferences): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>('/profile/preferences/notification', {
      notificationPreferences: preferences
    });
  }
}

